<template>
  <div>
    <v-btn @click="dialog = true" small><v-icon>mdi-plus</v-icon></v-btn>
    <v-dialog v-model="dialog" persistent width="1000">
      <v-card>
        <v-card-title>Add Return Item</v-card-title>
        <v-card-text>
          <v-form>
            <div class="d-flex">
              <v-text-field
                label="Item Name"
                v-model="all_items_search"
              ></v-text-field>
              <v-btn
                small
                color="primary"
                class="ml-3 mt-3"
                @click="openSerialScan"
              >
                <v-icon left small>mdi-barcode-scan</v-icon>
                Scan S/N
              </v-btn>
              <v-text-field
                label="Inserted By"
                class="ml-3"
                v-model="form.technician"
              ></v-text-field>
            </div>

            <v-data-table
              :items="all_items"
              :headers="all_items_headers"
              :search="all_items_search"
              :options="all_items_options"
            >
              <template v-slot:item.action="{ item }">
                <v-btn small @click="select(item)">Select</v-btn>
              </template>
              <template v-slot:item.item_name="{ item }">
                <span
                  :class="
                    item.is_vat
                      ? 'green--text font-weight-bold'
                      : 'orange--text font-weight-bold'
                  "
                  >{{ item.item_name }}</span
                >
              </template>
              <template v-slot:item.is_deleted="{ item }">
                <span v-if="item.is_deleted">
                  <v-chip color="red" class="white--text" x-small
                    >Deleted</v-chip
                  >
                </span>
                <span v-else>
                  <v-chip color="green" class="white--text" x-small
                    >Active</v-chip
                  >
                </span>
              </template>
            </v-data-table>

            <v-divider class="my-3"></v-divider>

            <div class="d-flex">
              <v-text-field
                v-model="form.item_name"
                readonly
                label="Item Name"
                class="mr-5"
              ></v-text-field>
              <v-text-field
                class="mr-5"
                label="Quantity"
                v-model="form.quantity"
                type="number"
              ></v-text-field>
              <v-text-field
                label="S/N"
                v-model="form.product_serial_number"
              ></v-text-field>
            </div>

            <!-- Optional: Link to specific stock lot -->
            <v-expansion-panels flat class="mb-3" v-if="form.item_id">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span>
                    <v-icon small class="mr-1">mdi-link-variant</v-icon>
                    Link to Stock Batch (Optional)
                    <span
                      v-if="form.item_stock_lot_id"
                      class="green--text ml-2"
                    >
                      — Batch #{{ form.item_stock_lot_id }} selected
                    </span>
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <p class="caption grey--text mb-2">
                    Select the stock batch this defective item came from (helps
                    trace supplier).
                  </p>
                  <v-data-table
                    :items="itemLots"
                    :headers="lotHeaders"
                    dense
                    :loading="lotsLoading"
                    :items-per-page="5"
                  >
                    <template v-slot:item.lot_action="{ item: lot }">
                      <v-btn
                        x-small
                        :color="
                          form.item_stock_lot_id === lot.id ? 'success' : 'grey'
                        "
                        :dark="form.item_stock_lot_id === lot.id"
                        @click="selectLot(lot)"
                      >
                        {{
                          form.item_stock_lot_id === lot.id
                            ? "Selected"
                            : "Select"
                        }}
                      </v-btn>
                    </template>
                    <template v-slot:item.remaining_quantity="{ item: lot }">
                      {{ lot.remaining_quantity }} / {{ lot.initial_quantity }}
                    </template>
                    <template v-slot:item.unit_cost="{ item: lot }">
                      ₱{{ Number(lot.unit_cost).toLocaleString() }}
                    </template>
                    <template v-slot:item.is_vat="{ item: lot }">
                      <v-chip
                        :color="lot.is_vat ? 'green' : 'orange'"
                        dark
                        x-small
                      >
                        {{ lot.is_vat ? "VAT" : "Non-VAT" }}
                      </v-chip>
                    </template>
                    <template v-slot:item.purchase_date="{ item: lot }">
                      {{
                        lot.purchase_date
                          ? lot.purchase_date.substring(0, 10)
                          : "N/A"
                      }}
                    </template>
                    <template v-slot:item.warranty_status="{ item: lot }">
                      <div>
                        <v-chip
                          v-if="lot.warranty_status === 'valid'"
                          color="green"
                          dark
                          x-small
                        >
                          Valid
                        </v-chip>
                        <v-chip
                          v-else-if="lot.warranty_status === 'expiring_soon'"
                          color="amber darken-2"
                          dark
                          x-small
                        >
                          {{ lot.warranty_days_remaining }}d left
                        </v-chip>
                        <div v-else-if="lot.warranty_status === 'expired'">
                          <v-chip color="red" dark x-small>Expired</v-chip>
                          <div class="red--text caption mt-1">
                            Supplier may reject RMA
                          </div>
                        </div>
                        <v-chip v-else color="grey" dark x-small>N/A</v-chip>
                      </div>
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Invoice Linkage for Warranty Check -->
            <v-expansion-panels flat class="mb-3" v-if="form.item_id">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span>
                    <v-icon small class="mr-1">mdi-shield-check</v-icon>
                    Link to Invoice (For Warranty Check)
                    <span v-if="selectedInvoiceId" class="green--text ml-2">
                      — Invoice #{{ selectedInvoiceId }} selected
                    </span>
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <p class="caption grey--text mb-2">
                    Select the customer invoice to check warranty coverage.
                  </p>
                  <v-text-field
                    v-model="invoiceSearch"
                    label="Search invoices..."
                    prepend-inner-icon="mdi-magnify"
                    dense
                    outlined
                    class="mb-2"
                  ></v-text-field>
                  <v-data-table
                    :items="customerInvoices"
                    :headers="invoiceHeaders"
                    :search="invoiceSearch"
                    dense
                    :loading="invoicesLoading"
                    :items-per-page="5"
                  >
                    <template v-slot:item.inv_action="{ item: inv }">
                      <v-btn
                        x-small
                        :color="
                          selectedInvoiceId === inv.id ? 'success' : 'grey'
                        "
                        :dark="selectedInvoiceId === inv.id"
                        @click="selectInvoice(inv)"
                      >
                        {{
                          selectedInvoiceId === inv.id ? "Selected" : "Select"
                        }}
                      </v-btn>
                    </template>
                    <template v-slot:item.warranty_policy_name="{ item: inv }">
                      <v-chip
                        v-if="inv.warranty_policy"
                        x-small
                        color="blue"
                        dark
                      >
                        {{ inv.warranty_policy.name }}
                      </v-chip>
                      <span v-else class="grey--text caption">None</span>
                    </template>
                  </v-data-table>

                  <!-- Warranty Status Banner -->
                  <div v-if="warrantyResult" class="mt-3">
                    <v-alert
                      v-if="warrantyResult.is_covered === true"
                      type="success"
                      dense
                      outlined
                    >
                      <strong>Warranty Valid</strong> —
                      {{ warrantyResult.policy_name }} ({{
                        warrantyResult.warranty_type
                      }}), {{ warrantyResult.days_remaining }} days remaining.
                      Expires: {{ warrantyResult.warranty_expiry }}
                    </v-alert>
                    <v-alert
                      v-else-if="warrantyResult.is_covered === false"
                      type="error"
                      dense
                      outlined
                    >
                      <strong>Warranty Expired</strong> —
                      {{ warrantyResult.policy_name }} ({{
                        warrantyResult.warranty_type
                      }}), expired
                      {{ Math.abs(warrantyResult.days_remaining) }} days ago.
                      Expired: {{ warrantyResult.warranty_expiry }}
                    </v-alert>
                    <v-alert
                      v-if="warrantyResult.classification_needed"
                      type="warning"
                      dense
                      outlined
                      class="mt-2"
                    >
                      Item not classified (major/minor) — using conservative
                      warranty estimate. Please classify this item in inventory
                      management.
                    </v-alert>
                  </div>
                  <v-alert
                    v-if="
                      selectedInvoiceId &&
                      warrantyResult &&
                      warrantyResult.is_covered === null
                    "
                    color="grey"
                    dense
                    outlined
                    class="mt-3"
                  >
                    {{
                      warrantyResult.message ||
                      "No warranty policy set on this invoice"
                    }}
                  </v-alert>
                  <div v-if="warrantyLoading" class="text-center my-3">
                    <v-progress-circular
                      indeterminate
                      size="24"
                      color="primary"
                    ></v-progress-circular>
                    <span class="ml-2 caption">Checking warranty...</span>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-textarea
              v-model="form.problem_description"
              label="Problem Description"
              placeholder="Please state the item's defect clearly. Also include the troubleshooting done."
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="submit" :loading="loading"
            >Submit</v-btn
          >
          <v-btn @click="dialog = false"> Close</v-btn>
          <v-spacer></v-spacer>
          <p class="caption grey--text">addReturnItemModal.vue</p>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SCAN-BY-SERIAL DIALOG (same pattern as addInvoiceCard.vue, JMB-242) -->
    <v-dialog
      v-model="serial_scan_dialog"
      max-width="520"
      @keydown.esc="serial_scan_dialog = false"
    >
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-barcode-scan</v-icon>
          Scan Serial to Select Item
          <v-spacer></v-spacer>
          <v-btn icon @click="serial_scan_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="serial_scan_input"
            ref="serialScanField"
            label="Scan or type a serial number"
            placeholder="Scan here, then press Enter"
            prepend-inner-icon="mdi-barcode-scan"
            outlined
            dense
            clearable
            autofocus
            hide-details
            :loading="serial_scan_loading"
            @keyup.enter="scanSerialToReturn"
          ></v-text-field>
          <div
            v-if="serial_scan_feedback"
            class="caption mt-2"
            :class="
              serial_scan_feedback_type === 'success'
                ? 'green--text'
                : 'red--text'
            "
          >
            <v-icon
              x-small
              :color="serial_scan_feedback_type === 'success' ? 'green' : 'red'"
              >{{
                serial_scan_feedback_type === "success"
                  ? "mdi-check-circle"
                  : "mdi-alert-circle"
              }}</v-icon
            >
            {{ serial_scan_feedback }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="serial_scan_dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { mapActions, mapGetters } from "vuex";
export default {
  data() {
    return {
      all_items_options: {
        itemsPerPage: 10,
      },
      all_items_search: "",
      all_items_headers: [
        { text: "", value: "action" },
        { text: "Id", value: "id" },
        { text: "Item Name", value: "item_name" },
        { text: "Batch ID", value: "import_batch.batch_description" },
        { text: "Quantity", value: "quantity" },
        { text: "Branch", value: "branch.branch_name" },
        { text: "Date Inserted", value: "created_at" },
        { text: "Status", value: "is_deleted" },
      ],
      // Invoice warranty check
      invoiceSearch: "",
      invoicesLoading: false,
      customerInvoices: [],
      selectedInvoiceId: null,
      warrantyResult: null,
      warrantyLoading: false,
      invoiceHeaders: [
        { text: "", value: "inv_action" },
        { text: "ID", value: "id" },
        { text: "Date", value: "invoice_date" },
        { text: "Purpose", value: "purpose" },
        { text: "Total", value: "total_amount" },
        {
          text: "Warranty Policy",
          value: "warranty_policy_name",
          sortable: false,
        },
      ],
      lotHeaders: [
        { text: "", value: "lot_action" },
        { text: "ID", value: "id" },
        { text: "Purchase Date", value: "purchase_date" },
        { text: "Supplier", value: "supplier" },
        { text: "Unit Cost", value: "unit_cost" },
        { text: "Stock", value: "remaining_quantity" },
        { text: "VAT", value: "is_vat" },
        { text: "Warranty", value: "warranty_status", sortable: false },
        { text: "Remarks", value: "remarks" },
      ],
      dialog: false,
      loading: false,
      lotsLoading: false,
      itemLots: [],
      // Scan-by-serial lookup
      serial_scan_dialog: false,
      serial_scan_input: "",
      serial_scan_loading: false,
      serial_scan_feedback: "",
      serial_scan_feedback_type: "success",
      form: {
        item_id: "",
        item_name: "",
        quantity: 1,
        product_serial_number: "",
        problem_description: "",
        technician: "",
        item_stock_lot_id: null,
        invoice_id: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      all_items: "item/all_items",
      user: "auth/user",
    }),
  },
  methods: {
    ...mapActions({
      add_returned_item: "item/add_returned_item",
      get_all_items: "item/get_all_items",
      get_stock_lots: "item/get_stock_lots",
    }),
    async submit() {
      if (!this.form.item_id) {
        this.$alert({
          title: "Validation",
          message: "Please select an item first.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      // Block RMA if linked invoice has expired warranty for this item
      if (
        this.selectedInvoiceId &&
        this.warrantyResult &&
        this.warrantyResult.is_covered === false
      ) {
        this.$alert({
          title: "Cannot File RMA — Warranty Expired",
          message:
            `The <b>${
              this.warrantyResult.warranty_type || "warranty"
            }</b> for this item has expired.<br><br>` +
            `<b>Policy:</b> ${this.warrantyResult.policy_name}<br>` +
            `<b>Invoice Date:</b> ${this.warrantyResult.invoice_date}<br>` +
            `<b>Warranty Expiry:</b> ${this.warrantyResult.warranty_expiry}<br>` +
            `<b>Expired:</b> ${Math.abs(
              this.warrantyResult.days_remaining
            )} day(s) ago<br><br>` +
            `This item's warranty coverage (<b>${this.warrantyResult.warranty_type}</b>) has expired. RMA cannot be filed.`,
          color: "error",
          icon: "mdi-shield-alert-outline",
        });
        return;
      }
      let x = await this.$confirm({
        title: "Add Return Entry",
        message: "Are you sure you want to add this entry?",
        color: "primary",
        icon: "mdi-help-circle-outline",
      });
      if (x) {
        this.loading = true;
        this.add_returned_item(this.form)
          .then(() => {
            this.$alert({
              title: "Success",
              message: "Entry Added",
              color: "success",
              icon: "mdi-check-circle",
            });
            this.resetForm();
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    openSerialScan() {
      this.serial_scan_input = "";
      this.serial_scan_feedback = "";
      this.serial_scan_dialog = true;
      this.$nextTick(() => {
        setTimeout(() => {
          const f = this.$refs.serialScanField;
          if (f && f.focus) f.focus();
        }, 200);
      });
    },
    // Scan a serial number to find its item, select it for the return entry,
    // and pre-fill the S/N field with the scanned serial.
    async scanSerialToReturn() {
      const raw = (this.serial_scan_input || "").trim();
      if (!raw) return;
      this.serial_scan_loading = true;
      this.serial_scan_feedback = "";
      try {
        const res = await axios.get("/item_by_serial", {
          params: { serial: raw },
        });
        const serial = res.data.serial_number;
        const foundItem = res.data.item;
        // Prefer the already-loaded item object so the table stays in sync;
        // fall back to the server copy if it isn't loaded.
        const item =
          this.all_items.find((i) => i.id === foundItem.id) || foundItem;
        await this.select(item);
        this.form.product_serial_number = serial;
        this.serial_scan_dialog = false;
        this.serial_scan_feedback_type = "success";
      } catch (e) {
        this.serial_scan_feedback_type = "error";
        if (e.response && e.response.status === 404) {
          this.serial_scan_feedback = `No in-stock item found for serial "${raw}".`;
        } else {
          this.serial_scan_feedback =
            (e.response && e.response.data && e.response.data.error) ||
            "Serial lookup failed. Please try again.";
        }
      }
      this.serial_scan_loading = false;
      this.serial_scan_input = "";
      if (this.serial_scan_dialog) {
        this.$nextTick(() => {
          const f = this.$refs.serialScanField;
          if (f && f.focus) f.focus();
        });
      }
    },
    async select(item) {
      this.form.item_id = item.id;
      this.form.item_name = item.item_name;
      this.form.item_stock_lot_id = null;
      this.selectedInvoiceId = null;
      this.form.invoice_id = null;
      this.warrantyResult = null;
      this.customerInvoices = [];
      // Load stock lots for this item
      this.lotsLoading = true;
      try {
        this.itemLots = await this.get_stock_lots(item.id);
      } catch (err) {
        console.error("Failed to load lots:", err);
        this.itemLots = [];
      }
      this.lotsLoading = false;
      // Load invoices containing this item
      this.loadInvoicesForItem(item.id);
    },
    async loadInvoicesForItem(itemId) {
      this.invoicesLoading = true;
      try {
        // Fetch both unpaid and paid invoices, filter for those containing this item
        let [unpaid, paid] = await Promise.all([
          axios.get("/all_invoices"),
          axios.get("/paid_invoices"),
        ]);
        let allInvoices = [...unpaid.data, ...paid.data];
        this.customerInvoices = allInvoices.filter(
          (inv) =>
            inv.invoice_items &&
            inv.invoice_items.some((ii) => ii.item_id == itemId)
        );
      } catch (err) {
        console.error("Failed to load invoices:", err);
        this.customerInvoices = [];
      }
      this.invoicesLoading = false;
    },
    async selectInvoice(inv) {
      if (this.selectedInvoiceId === inv.id) {
        this.selectedInvoiceId = null;
        this.form.invoice_id = null;
        this.warrantyResult = null;
        return;
      }
      this.selectedInvoiceId = inv.id;
      this.form.invoice_id = inv.id;
      // Check warranty
      this.warrantyLoading = true;
      this.warrantyResult = null;
      try {
        let response = await axios.get(`/invoice/${inv.id}/warranty_check`, {
          params: { item_id: this.form.item_id },
        });
        this.warrantyResult = response.data;
      } catch (err) {
        console.error("Warranty check failed:", err);
        this.warrantyResult = {
          is_covered: null,
          message: "Failed to check warranty",
        };
      }
      this.warrantyLoading = false;
    },
    selectLot(lot) {
      if (this.form.item_stock_lot_id === lot.id) {
        this.form.item_stock_lot_id = null;
      } else {
        this.form.item_stock_lot_id = lot.id;
      }
    },
    resetForm() {
      this.form = {
        item_id: "",
        item_name: "",
        quantity: 1,
        product_serial_number: "",
        problem_description: "",
        technician: this.user.full_name,
        item_stock_lot_id: null,
        invoice_id: null,
      };
      this.itemLots = [];
      this.customerInvoices = [];
      this.selectedInvoiceId = null;
      this.warrantyResult = null;
    },
  },
  created() {
    if (this.all_items.length <= 0) {
      this.get_all_items();
    }
    this.form.technician = this.user.full_name;
  },
};
</script>

<style lang="scss" scoped></style>
